import { AppHTTPService } from '@/api/http-service'
import { RenameDialog } from '@/components/RenameDialog/RenameDialog'
import { Sheet } from '@/contexts/SheetContext'
import { cn, Text } from '@flatfile/baseline'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '@/auth/AuthProvider'

type SheetTabItemProps = {
  path: string
  sheetId: string
  sheet: Sheet
  onRename: (sheetId: string, newName: string) => Promise<void>
}

type TabStripProps = {
  children: React.ReactNode
  className?: string
}

export const TabStrip = ({ children, className }: TabStripProps) => {
  return (
    <div className={cn('relative mb-4', className)} data-testid='tab-strip'>
      <div className='flex flex-nowrap items-start ml-2 gap-1 justify-start whitespace-nowrap overflow-x-auto overflow-y-hidden'>
        {children}
      </div>
    </div>
  )
}

type TabStripItemProps = {
  children: React.ReactNode
  isActive: boolean
  className?: string
}

export const TabStripItem = ({
  children,
  isActive,
  className,
}: TabStripItemProps) => {
  return (
    <div
      data-override-targetid={`ff_tab_strip_item`}
      className={cn(
        'flex color-muted p-2 relative overflow-hidden flex-shrink-0 hover:no-underline group',
        className
      )}
    >
      {children}
      <div
        data-override-targetid={`ff_tab_strip_item_underline_${isActive ? 'active' : 'inactive'}`}
        className={cn(
          'absolute bottom-0 left-0 w-full h-0.5 bg-transparent group-hover:bg-primary-100 transition-colors duration-200 ease-in-out',
          {
            'bg-primary-100': isActive,
          }
        )}
      />
    </div>
  )
}

const constructSheetLink = (
  path: string,
  projectId: string,
  fileId: string,
  sheetId: string
) => {
  return `${path}/projects/${projectId}/files/${fileId}/sheets/${sheetId}/prepare-chat`
}

const SheetTabItem = ({
  path,
  sheetId,
  sheet,
  onRename,
}: SheetTabItemProps) => {
  const { sheetId: currentSheetId, projectId, fileId } = useParams()
  const isActive = currentSheetId === sheetId
  const [isRenaming, setIsRenaming] = useState(false)

  const handleRename = async (newName: string) => {
    await onRename(sheetId, newName)
  }

  return (
    <RenameDialog
      isOpen={isRenaming}
      onOpenChange={setIsRenaming}
      currentName={sheet.name}
      onRename={handleRename}
      title='Sheet Name'
      trigger={
        <Link
          className='hover:no-underline cursor-pointer'
          to={constructSheetLink(path, projectId, fileId, sheetId)}
          title={sheet?.name}
          onDoubleClick={() => setIsRenaming(true)}
        >
          <TabStripItem isActive={isActive}>
            <Text
              as='div'
              data-override-targetid={`ff_tab_strip_item_text_${
                isActive ? 'active' : 'inactive'
              }`}
              className='truncate max-w-48'
              color={isActive ? 'primary-100' : 'muted-foreground-100'}
              emphasized={isActive}
            >
              {sheet?.name}
            </Text>
          </TabStripItem>
        </Link>
      }
    />
  )
}

export const SheetTabs = ({
  fileId,
  path,
}: {
  fileId: string
  path: string
}) => {
  const queryClient = useQueryClient()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { data: sheetsResponse } = useQuery({
    queryKey: ['sheets', fileId],
    queryFn: () => AppHTTPService.client.sheets.list(fileId),
    enabled:
      !!fileId &&
      fileId !== 'undefined' &&
      fileId !== '' &&
      isAuthenticated &&
      !authLoading,
  })

  const sheets = useMemo(() => sheetsResponse?.data, [sheetsResponse])

  const handleRename = async (sheetId: string, newName: string) => {
    await AppHTTPService.client.sheets.update(sheetId, {
      name: newName,
    })
    // Invalidate and refetch sheets list
    await queryClient.invalidateQueries({ queryKey: ['sheets', fileId] })
  }

  return (
    <>
      <TabStrip className='absolute bottom-0 left-0 right-0 mb-0 bg-primary-foreground-90 border-t '>
        {sheets?.map((sheet) => (
          <SheetTabItem
            key={sheet.id}
            sheetId={sheet.id}
            sheet={sheet}
            path={path}
            onRename={handleRename}
          />
        ))}
      </TabStrip>
    </>
  )
}
